import { formatCompactCurrency } from "../Helper/formatCompactCurrency";

/**
 * Enhanced placeholder values that should be considered invalid
 */
const INVALID_PLACEHOLDER_VALUES = [
  "n/a",
  "na",
  "not available",
  "not specified",
  "unknown",
  "null",
  "undefined",
  "none",
  "no data available",
  "no information",
  "data not found",
  "tbd",
  "tba",
  "not disclosed",
  "not reported",
  "not applicable",
  "none available",
  "empty",
  "no data",
  "unavailable",
  "pending",
  "not provided",
  "--",
  "---",
  "...",
];

// Extract flattened field data - API version of the function from Financial React components
export function extractFlattenedFieldAPI(
  data: any,
  fieldPath: string,
  fallback: any = null
): any {
  if (!data || typeof data !== "object") return fallback;

  // Try direct flattened field access first (e.g., data['metadata.confidence_score'])
  if (data[fieldPath] !== undefined && data[fieldPath] !== null) {
    return data[fieldPath];
  }

  // Try nested object access as fallback (e.g., data.metadata.confidence_score)
  const pathParts = fieldPath.split(".");
  let current = data;

  for (const part of pathParts) {
    if (current && typeof current === "object" && current[part] !== undefined) {
      current = current[part];
    } else {
      return fallback;
    }
  }

  return current !== undefined ? current : fallback;
}

/**
 * Check if value is a business-meaningful value (context-aware for financial data)
 */
export function isBusinessMeaningfulValue(
  value: any,
  context?: string
): boolean {
  if (value === null || value === undefined) return false;

  if (typeof value === "number") {
    if (isNaN(value) || !isFinite(value)) return false;
    // For financial contexts, zero might be meaningful, but -999999 style placeholders are not
    if (Math.abs(value) > 1e15) return false; // Suspiciously large numbers
    return true;
  }

  if (typeof value === "string") {
    const cleaned = value.trim().toLowerCase();
    if (cleaned === "" || INVALID_PLACEHOLDER_VALUES.includes(cleaned))
      return false;
    // Check for obvious placeholder patterns
    if (/^[0-9\-_]+$/.test(cleaned) && cleaned.length > 10) return false; // Long sequences of digits/dashes
    return true;
  }

  return value !== null && value !== undefined;
}

/**
 * Check if field is valid for rendering - enhanced version
 */
export function isValidField(value: any, context?: string): boolean {
  if (value === null || value === undefined) return false;

  if (typeof value === "string") {
    const cleaned = value.trim().toLowerCase();
    if (cleaned === "" || INVALID_PLACEHOLDER_VALUES.includes(cleaned))
      return false;

    // Check for percentage strings that are actually "0%" or invalid
    if (
      context === "percentage" &&
      (cleaned === "0%" || cleaned === "0.0%" || cleaned === "0.00%")
    )
      return false;

    return true;
  }

  if (typeof value === "number") {
    if (isNaN(value) || !isFinite(value)) return false;

    // Context-aware zero handling
    if (value === 0) {
      // For some contexts, zero is meaningful, for others it's not
      if (context === "growth" || context === "percentage") return false;
      if (context === "price" || context === "revenue") return false;
    }

    return true;
  }

  if (Array.isArray(value)) {
    return value.length > 0 && value.some(item => isValidField(item, context));
  }

  if (typeof value === "object") {
    return Object.values(value).some(val => isValidField(val, context));
  }

  return true;
}

/**
 * Specialized validation for earnings data
 */
export function hasValidEarningsContent(
  annualEarnings: any[],
  quarterlyEarnings: any[]
): boolean {
  const hasValidAnnual =
    Array.isArray(annualEarnings) &&
    annualEarnings.length > 0 &&
    annualEarnings.some(earning =>
      isBusinessMeaningfulValue(earning?.reportedEPS, "financial")
    );

  const hasValidQuarterly =
    Array.isArray(quarterlyEarnings) &&
    quarterlyEarnings.length > 0 &&
    quarterlyEarnings.some(earning =>
      isBusinessMeaningfulValue(earning?.reportedEPS, "financial")
    );

  return hasValidAnnual || hasValidQuarterly;
}

/**
 * Specialized validation for risk data
 */
export function hasValidRiskContent(
  keyRisks: any[],
  opportunities: any[],
  riskLevel: any
): boolean {
  const hasValidRisks =
    Array.isArray(keyRisks) &&
    keyRisks.length > 0 &&
    keyRisks.some(
      risk =>
        isBusinessMeaningfulValue(risk) &&
        typeof risk === "string" &&
        risk.length > 0
    );

  const hasValidOpportunities =
    Array.isArray(opportunities) &&
    opportunities.length > 0 &&
    opportunities.some(
      opp =>
        isBusinessMeaningfulValue(opp) &&
        typeof opp === "string" &&
        opp.length > 0
    );

  const hasValidRiskLevel =
    isBusinessMeaningfulValue(riskLevel) &&
    typeof riskLevel === "string" &&
    !INVALID_PLACEHOLDER_VALUES.includes(riskLevel.toLowerCase());

  return hasValidRisks || hasValidOpportunities || hasValidRiskLevel;
}

/**
 * Specialized validation for trend data
 */
export function hasValidTrendContent(
  marginsQuarterly: any[],
  revenueQuarterly: any[]
): boolean {
  const hasValidMargins =
    Array.isArray(marginsQuarterly) &&
    marginsQuarterly.length > 0 &&
    marginsQuarterly.some(
      margin =>
        isBusinessMeaningfulValue(margin?.net_margin_change, "percentage") ||
        isBusinessMeaningfulValue(margin?.gross_margin_change, "percentage") ||
        isBusinessMeaningfulValue(margin?.operating_margin_change, "percentage")
    );

  const hasValidRevenue =
    Array.isArray(revenueQuarterly) &&
    revenueQuarterly.length > 0 &&
    revenueQuarterly.some(revenue =>
      isBusinessMeaningfulValue(revenue?.revenue, "financial")
    );

  return hasValidMargins || hasValidRevenue;
}

// Format currency values for API display
export function formatCurrencyAPI(value: any): string {
  if (!isValidField(value)) return "N/A";

  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return "N/A";

  return formatCompactCurrency(numValue, { fallback: "N/A" });
}

// Format percentage values for API display
export function formatPercentageAPI(value: any): string {
  if (!isValidField(value)) return "N/A";

  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return "N/A";

  // Handle both decimal (0-1) and percentage (0-100) formats
  const percentValue = numValue <= 1 ? numValue * 100 : numValue;
  return `${percentValue.toFixed(2)}%`;
}

// Format numeric values for API display
export function formatNumericAPI(value: any): string {
  if (!isValidField(value)) return "N/A";

  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return "N/A";

  return numValue.toLocaleString();
}

// Format date values for API display (converts 2025-08-25 to Aug 25, 2025)
export function formatDateAPI(value: any): string {
  if (!value) return "N/A";

  try {
    let dateObj: Date;

    // Handle various date formats
    if (typeof value === "string") {
      // Handle ISO format (2025-08-25T00:00:00Z or 2025-08-25)
      if (value.includes("T") || value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        dateObj = new Date(value);
      }
      // Handle date ranges like "2025-08-20 to 2025-08-26"
      else if (value.includes(" to ")) {
        const [startDate] = value.split(" to ");
        dateObj = new Date(startDate.trim());
      }
      // Handle other string formats
      else {
        dateObj = new Date(value);
      }
    } else if (value instanceof Date) {
      dateObj = value;
    } else {
      return String(value);
    }

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return String(value);
    }

    // Format as "Aug 25, 2025"
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return dateObj.toLocaleDateString("en-US", options);
  } catch (error) {
    console.error("Date formatting error:", error);
    return String(value);
  }
}

// Format date ranges for API display (converts "2025-08-20 to 2025-08-26" to "Aug 20, 2025 to Aug 26, 2025")
export function formatDateRangeAPI(value: any): string {
  if (!value || typeof value !== "string") return "N/A";

  if (value.includes(" to ")) {
    const [startDate, endDate] = value.split(" to ").map(d => d.trim());
    const formattedStart = formatDateAPI(startDate);
    const formattedEnd = formatDateAPI(endDate);

    if (formattedStart !== "N/A" && formattedEnd !== "N/A") {
      return `${formattedStart} to ${formattedEnd}`;
    }
  }

  // Single date
  return formatDateAPI(value);
}

// ✨ Audio Content Extraction for News Modal - New for News Section
export function extractNewsModalAudioAPI(
  data: any,
  organizationName?: string
): string {
  let audioContent = "";

  // Start with synopsis if available
  const synopsis = extractFlattenedFieldAPI(data, "synopsis", null);
  if (isValidField(synopsis)) {
    audioContent = synopsis;
  }

  // Add news metrics summary
  const totalArticles = extractFlattenedFieldAPI(
    data,
    "newsMetrics.totalArticles",
    null
  );
  const mediaVelocity = extractFlattenedFieldAPI(
    data,
    "newsMetrics.mediaVelocity",
    null
  );
  const overallSentiment = extractFlattenedFieldAPI(
    data,
    "sentimentAnalysis.overallSentiment",
    null
  );

  const metricsInfo = [];
  if (isValidField(totalArticles))
    metricsInfo.push(`${totalArticles} articles analyzed`);
  if (isValidField(mediaVelocity))
    metricsInfo.push(`${mediaVelocity} media velocity`);
  if (isValidField(overallSentiment))
    metricsInfo.push(`${overallSentiment} sentiment`);

  if (metricsInfo.length > 0) {
    if (audioContent) audioContent += ". ";
    audioContent += `News metrics show: ${metricsInfo.join(", ")}.`;
  }

  // Add sentiment themes
  const positiveThemes = extractFlattenedFieldAPI(
    data,
    "sentimentAnalysis.positiveThemes",
    []
  );
  const negativeThemes = extractFlattenedFieldAPI(
    data,
    "sentimentAnalysis.negativeThemes",
    []
  );

  if (Array.isArray(positiveThemes) && positiveThemes.length > 0) {
    const validPositive = positiveThemes.filter(theme => isValidField(theme));
    if (validPositive.length > 0) {
      if (audioContent) audioContent += " ";
      audioContent += `Positive themes include: ${validPositive.slice(0, 3).join(", ")}.`;
    }
  }

  if (Array.isArray(negativeThemes) && negativeThemes.length > 0) {
    const validNegative = negativeThemes.filter(theme => isValidField(theme));
    if (validNegative.length > 0) {
      if (audioContent) audioContent += " ";
      audioContent += `Areas of concern: ${validNegative.slice(0, 3).join(", ")}.`;
    }
  }

  // Add organization relevance if available
  if (organizationName) {
    const relevance =
      extractFlattenedFieldAPI(
        data,
        "organizationContext.relevantToOrganization.relevance",
        null
      ) ||
      extractFlattenedFieldAPI(
        data,
        "organizationContext.relevantToOrganization",
        null
      );
    if (isValidField(relevance)) {
      if (audioContent) audioContent += " ";
      audioContent += `This is particularly relevant to ${organizationName}: ${relevance}`;
    }

    // Add potential risks
    const potentialRisks = extractFlattenedFieldAPI(
      data,
      "organizationContext.potentialRisks",
      []
    );
    if (Array.isArray(potentialRisks) && potentialRisks.length > 0) {
      const validRisks = potentialRisks.filter(risk => isValidField(risk));
      if (validRisks.length > 0) {
        if (audioContent) audioContent += " ";
        audioContent += `Potential risks: ${validRisks.slice(0, 2).join(", ")}.`;
      }
    }

    // Add potential opportunities
    const potentialOpportunities = extractFlattenedFieldAPI(
      data,
      "organizationContext.potentialOpportunities",
      []
    );
    if (
      Array.isArray(potentialOpportunities) &&
      potentialOpportunities.length > 0
    ) {
      const validOpportunities = potentialOpportunities.filter(opp =>
        isValidField(opp)
      );
      if (validOpportunities.length > 0) {
        if (audioContent) audioContent += " ";
        audioContent += `Opportunities identified: ${validOpportunities.slice(0, 2).join(", ")}.`;
      }
    }
  }

  // Fallback if no content found
  if (!audioContent) {
    audioContent = "News analysis summary not available.";
  }

  return audioContent;
}

export function extractLeadershipAudioAPI(data: any): string {
  // First try to get synopsis from the data
  const synopsis =
    extractFlattenedFieldAPI(data, "synopsis", null) ||
    extractFlattenedFieldAPI(data, "leadership.synopsis", null);

  if (isValidField(synopsis)) return synopsis;

  // Fallback to building narrative from available fields
  let narrative = "";

  const changes =
    extractFlattenedFieldAPI(data, "executive_changes", []) ||
    extractFlattenedFieldAPI(data, "executiveChanges", []);
  if (Array.isArray(changes) && changes.length > 0) {
    narrative += `Recent leadership changes include ${changes.length} executive updates. `;
  }

  const trends = extractFlattenedFieldAPI(data, "trends", null);
  if (isValidField(trends)) {
    narrative += `Leadership trends show ${trends}. `;
  }

  const kpis = extractFlattenedFieldAPI(data, "kpis", null);
  if (kpis) {
    const newHires = extractFlattenedFieldAPI(kpis, "newHires", null);
    const executiveTurnover = extractFlattenedFieldAPI(
      kpis,
      "executiveTurnover",
      null
    );
    if (newHires) narrative += `New hires: ${newHires}. `;
    if (executiveTurnover)
      narrative += `Executive turnover: ${executiveTurnover}%. `;
  }

  return (
    narrative ||
    "Leadership analysis is being processed. Please check back later for updated information."
  );
}
